import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Search, CalendarDays, Newspaper, Briefcase, Building2 } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";

interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  type: "event" | "news" | "career" | "member";
  url: string;
}

export function SearchCommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const pattern = `%${q}%`;

    const [events, news, careers, members] = await Promise.all([
      (supabase as any).from("events").select("id, title, location").eq("is_active", true).ilike("title", pattern).limit(5),
      (supabase as any).from("news_updates").select("id, title, category").eq("is_active", true).ilike("title", pattern).limit(5),
      (supabase as any).from("job_postings").select("id, title, organization").eq("is_active", true).ilike("title", pattern).limit(5),
      (supabase as any).from("member_organizations").select("id, name, membership_type").eq("is_active", true).ilike("name", pattern).limit(5),
    ]);

    const mapped: SearchResult[] = [
      ...(events.data || []).map((e: any) => ({ id: e.id, title: e.title, subtitle: e.location, type: "event" as const, url: "/events" })),
      ...(news.data || []).map((n: any) => ({ id: n.id, title: n.title, subtitle: n.category, type: "news" as const, url: `/news/${n.id}` })),
      ...(careers.data || []).map((c: any) => ({ id: c.id, title: c.title, subtitle: c.organization, type: "career" as const, url: "/careers" })),
      ...(members.data || []).map((m: any) => ({ id: m.id, title: m.name, subtitle: m.membership_type, type: "member" as const, url: "/membership/members" })),
    ];

    setResults(mapped);
    setLoading(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query, search]);

  const handleSelect = (url: string) => {
    setOpen(false);
    setQuery("");
    navigate(url);
  };

  const icon = (type: string) => {
    switch (type) {
      case "event": return <CalendarDays className="mr-2 h-4 w-4 text-muted-foreground" />;
      case "news": return <Newspaper className="mr-2 h-4 w-4 text-muted-foreground" />;
      case "career": return <Briefcase className="mr-2 h-4 w-4 text-muted-foreground" />;
      case "member": return <Building2 className="mr-2 h-4 w-4 text-muted-foreground" />;
      default: return null;
    }
  };

  const groupLabel = (type: string) => {
    switch (type) {
      case "event": return "Events";
      case "news": return "News";
      case "career": return "Careers";
      case "member": return "Member Organizations";
      default: return type;
    }
  };

  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    (acc[r.type] = acc[r.type] || []).push(r);
    return acc;
  }, {});

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="hidden md:flex items-center gap-2 text-muted-foreground h-8 px-3"
        onClick={() => setOpen(true)}
      >
        <Search className="h-3.5 w-3.5" />
        <span className="text-xs">Search...</span>
        <kbd className="pointer-events-none ml-2 inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          ⌘K
        </kbd>
      </Button>

      <Button
        variant="ghost"
        size="icon"
        className="md:hidden h-8 w-8"
        onClick={() => setOpen(true)}
        aria-label="Search"
      >
        <Search className="h-4 w-4" />
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search events, news, careers, members..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {query.length < 2 ? (
            <CommandEmpty>Type at least 2 characters to search...</CommandEmpty>
          ) : loading ? (
            <CommandEmpty>Searching...</CommandEmpty>
          ) : results.length === 0 ? (
            <CommandEmpty>No results found.</CommandEmpty>
          ) : (
            Object.entries(grouped).map(([type, items], idx) => (
              <div key={type}>
                {idx > 0 && <CommandSeparator />}
                <CommandGroup heading={groupLabel(type)}>
                  {items.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={`${item.title} ${item.subtitle || ""}`}
                      onSelect={() => handleSelect(item.url)}
                    >
                      {icon(type)}
                      <div className="flex flex-col">
                        <span>{item.title}</span>
                        {item.subtitle && (
                          <span className="text-xs text-muted-foreground">{item.subtitle}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </div>
            ))
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
